"use client";

import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { AccessDeniedNotice } from "@/app/auth/login/_components/access-denied-notice";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth/client";
import {
  LOGIN_ERROR_CODE,
  type LoginErrorCode,
  MAZRICA_ACCOUNT_SWITCH,
} from "@/lib/auth/login-error-codes";
import { buildAuthCallbackURL } from "@/lib/auth/return-path";
import { getPlatformConfig } from "@/lib/config/platform";

const isLocalAuth = getPlatformConfig().authMode === "local";

const LOGIN_ERROR_CODES = new Set<string>(Object.values(LOGIN_ERROR_CODE));

function parseLoginErrorCode(value: string | null): LoginErrorCode | null {
  return value && LOGIN_ERROR_CODES.has(value)
    ? (value as LoginErrorCode)
    : null;
}

type SocialSignInResult = Awaited<ReturnType<typeof authClient.signIn.social>>;

function getSocialRedirectUrl(result: SocialSignInResult) {
  const data = result.data as
    | {
        redirect?: unknown;
        url?: unknown;
      }
    | null
    | undefined;

  if (data?.redirect !== true || typeof data.url !== "string") {
    return null;
  }

  try {
    const url = new URL(data.url, window.location.href);
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.href
      : null;
  } catch {
    return null;
  }
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [oidcLoading, setOidcLoading] = useState(false);
  const [error, setError] = useState("");
  const [oidcError, setOidcError] = useState("");
  const [loginErrorCode, setLoginErrorCode] = useState<LoginErrorCode | null>(
    null,
  );
  const [accountSwitchRequired, setAccountSwitchRequired] = useState(false);
  const [hasLoadedLoginParams, setHasLoadedLoginParams] = useState(false);
  const hasStartedIframeOidcLogin = useRef(false);

  const getCallbackURL = () => {
    if (typeof window === "undefined") {
      return "/";
    }

    const searchParams = new URLSearchParams(window.location.search);
    return buildAuthCallbackURL(
      searchParams.get("returnTo") ?? searchParams.get("callbackUrl"),
      window.location.origin,
    );
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    setLoginErrorCode(parseLoginErrorCode(searchParams.get("error")));
    setAccountSwitchRequired(
      searchParams.get(MAZRICA_ACCOUNT_SWITCH.queryParam) ===
        MAZRICA_ACCOUNT_SWITCH.queryValue,
    );
    setHasLoadedLoginParams(true);
  }, []);

  const handleMazricaLogin = async () => {
    setOidcLoading(true);
    setOidcError("");

    try {
      const result = await authClient.signIn.social({
        provider: "mazrica-id",
        callbackURL: getCallbackURL(),
      });

      if (result.error) {
        setOidcError("Mazrica ID ログインを開始できませんでした。");
        setOidcLoading(false);
        return;
      }

      const redirectUrl = getSocialRedirectUrl(result);
      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }
    } catch {
      setOidcError("Mazrica ID ログインを開始できませんでした。");
    }

    setOidcLoading(false);
  };

  const handleLocalLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const result = await authClient.signIn.email({
        email,
        password,
        callbackURL: getCallbackURL(),
      });

      if (result.error) {
        setError(result.error.message || "Login failed");
      }
    } catch {
      setError("Login failed");
    } finally {
      setLoading(false);
    }
  };

  // Mazrica Sales に iframe 埋め込みされた場合は、ログイン画面を経由せず
  // Mazrica ID の既存ログインセッションで認証を開始する。
  useEffect(() => {
    if (
      !hasLoadedLoginParams ||
      window.self === window.top ||
      loginErrorCode ||
      accountSwitchRequired ||
      hasStartedIframeOidcLogin.current
    ) {
      return;
    }

    hasStartedIframeOidcLogin.current = true;
    void handleMazricaLogin();
  }, [accountSwitchRequired, hasLoadedLoginParams, loginErrorCode]);

  return (
    <main
      aria-labelledby="login-title"
      className="flex min-h-svh items-center justify-center bg-background px-4"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <h1 id="login-title" className="text-2xl font-medium">
            Sozo AI
          </h1>
          <CardDescription>
            {loginErrorCode
              ? "アクセスできない理由をご確認ください"
              : "ログインしてください"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loginErrorCode && <AccessDeniedNotice code={loginErrorCode} />}

          {accountSwitchRequired && (
            <p
              role="status"
              className="rounded-md border bg-muted/50 px-3 py-3 text-sm text-foreground"
            >
              Sozo AIからログアウトしました。Mazrica
              Salesで利用するアカウントに切り替えてから、下のボタンを押してください。
            </p>
          )}

          {!loginErrorCode && (
            <>
              <Button
                onClick={handleMazricaLogin}
                className="w-full cursor-pointer"
                size="lg"
                disabled={oidcLoading}
                aria-describedby={oidcError ? "oidc-login-error" : undefined}
              >
                {oidcLoading ? (
                  <>
                    <Loader2
                      aria-hidden="true"
                      className="mr-2 h-4 w-4 animate-spin"
                    />
                    ログイン中…
                  </>
                ) : (
                  "Mazrica ID でログイン"
                )}
              </Button>
              {oidcError && (
                <p
                  id="oidc-login-error"
                  role="alert"
                  className="text-sm text-destructive"
                >
                  {oidcError}
                </p>
              )}

              {isLocalAuth && (
                <>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-sm uppercase">
                      <span className="bg-background px-2 text-muted-foreground">
                        または
                      </span>
                    </div>
                  </div>
                  <form onSubmit={handleLocalLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">メールアドレス</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="dev@example.com"
                        aria-invalid={error ? true : undefined}
                        aria-describedby={
                          error ? "local-login-error" : undefined
                        }
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">パスワード</Label>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="password"
                        aria-invalid={error ? true : undefined}
                        aria-describedby={
                          error ? "local-login-error" : undefined
                        }
                        required
                      />
                    </div>

                    {error && (
                      <p
                        id="local-login-error"
                        role="alert"
                        className="text-sm text-destructive"
                      >
                        ログインに失敗しました
                      </p>
                    )}

                    <Button
                      type="submit"
                      className="w-full cursor-pointer"
                      variant="secondary"
                      disabled={loading}
                    >
                      {loading ? "ログイン中…" : "メールアドレスでログイン"}
                    </Button>

                    <p className="text-sm text-muted-foreground text-center">
                      ローカル開発モード
                    </p>
                  </form>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
